package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import top.wcpe.allincore.ui.runtime.style.StyleVisual
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import top.wcpe.allincore.ui.runtime.theme.StateLayer
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess

/**
 * GPU 特效合成工具：在视觉绘制完成后追加 neon / flow / energy / ripple 等装饰效果。
 *
 * 由 [BoxRenderer] / [ButtonRenderer] / [ProgressBarRenderer] 共用，避免模板代码重复。所有
 * `applyXxxIfConfigured` 方法支持 [CornerRadii] 圆角覆盖：Button 的圆角由 [ShapeResolver] 决定
 * （未写回 [StyleVisual]），故调用方需把算出的 cornerRadii 显式传入，让 shader 与控件几何对齐。
 */
@Suppress("MagicNumber")
internal object EffectsCompositor {

    // ========== theme scanline ==========

    /**
     * 主题扫描线：当前主题开启 scanline（[ControlTheme.scanlineDensity] > 0 且 scanlineColor 不透明）时，
     * 复用程序纹理路径叠加一层低 alpha 的 `scanline` 纹理。仅供有可见背景/surface 的容器（[BoxRenderer]）
     * 调用；元素自身已配置程序纹理（`effect.texture.kind`）时跳过避免双重纹理冲突。
     *
     * @param overrideCornerRadii 圆角覆盖，null 时取 [StyleVisual] 四角，使扫描线随容器圆角裁剪
     */
    fun applyThemeScanlineIfConfigured(
        box: Box,
        bridge: UiRenderBridge,
        visual: StyleVisual,
        overrideCornerRadii: CornerRadii?,
    ) {
        // 元素自带程序纹理时不再叠主题扫描线，避免两层纹理互相干扰
        if (!visual.proceduralTextureKind().isNullOrBlank()) return
        val theme = ThemeAccess.current()
        if (theme.scanlineDensity <= 0f) return
        if ((theme.scanlineColor ushr 24) == 0) return
        // colorB 取透明：scanline shader 仅用 colorA 作线色，行间留空透出底色；
        // scale 复用 StyleVisual 默认 8，density 取主题 scanlineDensity，intensity 固定 1。
        bridge.applyProceduralTexture(
            box.x(), box.y(), box.width(), box.height(),
            "scanline", theme.scanlineColor, 0x00000000,
            visual.proceduralScale(), theme.scanlineDensity, 1f,
            resolveCornerRadii(visual, overrideCornerRadii),
        )
    }

    // ========== ripple ==========

    /** [RippleAnimator] 中存在该元素的活跃 ripple 时，逐条绘制涟漪（带圆角裁剪）。 */
    fun applyRippleIfActive(
        element: UIElement<*>,
        box: Box,
        bridge: UiRenderBridge,
        overrideCornerRadii: CornerRadii? = null,
    ) {
        val theme = ThemeAccess.current()
        val visual = element.style().visual()
        val cornerRadii = resolveCornerRadii(visual, overrideCornerRadii)
        for (state in RippleAnimator.active(element)) {
            bridge.applyRippleEffect(
                box.x(), box.y(), box.width(), box.height(),
                resolveRippleColor(state, theme), state.progress(), state.radius(),
                state.centerX(), state.centerY(), cornerRadii,
            )
        }
    }

    /** 圆角解析：override 优先（按钮 ShapeResolver 路径），否则用 [StyleVisual] 四角字段。 */
    private fun resolveCornerRadii(visual: StyleVisual, override: CornerRadii?): FloatArray =
        override?.toArray() ?: visual.cornerRadii().toArray()

    private fun resolveRippleColor(state: RippleAnimator.RippleState, theme: ControlTheme): Int {
        val color = state.color()
        val fadeRatio = state.alpha() / RippleAnimator.RIPPLE_PRESSED_ALPHA
        if ((color ushr 24) == 0) {
            return StateLayer.withAlpha(theme.primary, theme.stateLayerPressedOpacity * fadeRatio)
        }
        return StateLayer.withAlpha(color, fadeRatio)
    }
}
